import React, { useEffect, useState } from 'react';

const NewEngineeringResponse = () => {
  const [activeStep, setActiveStep] = useState(0);

  const colors = {
    white: '#ffffff',
    bg: '#f7fbff',
    bgBlue: '#eef8ff',
    blue: '#0284c7',
    blueDark: '#0369a1',
    cyan: '#38bdf8',
    navy: '#0f172a',
    slate: '#475569',
    muted: '#64748b',
    border: '#dbeafe',
    borderLight: '#e2e8f0',
    green: '#0f766e',
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 6);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const stages = [
    {
      number: '01',
      title: 'Understand',
      label: 'Project Brief',
      icon: '⌁',
      description:
        'We understand your project vision, requirements, site conditions, design objectives, budget constraints, and construction expectations.',
    },
    {
      number: '02',
      title: 'Investigate',
      label: 'Engineering Inputs',
      icon: '⌬',
      description:
        'We evaluate loads, materials, geometry, applicable codes, site information, architectural requirements, and engineering constraints.',
    },
    {
      number: '03',
      title: 'Analyze',
      label: 'Structural Behaviour',
      icon: '◉',
      description:
        'Advanced engineering analysis is performed to understand structural behaviour, load paths, stability, strength, and serviceability.',
    },
    {
      number: '04',
      title: 'Design',
      label: 'Optimized Solution',
      icon: '◇',
      description:
        'We convert analysis results into safe, economical, optimized structural systems designed for practical construction.',
    },
    {
      number: '05',
      title: 'Model',
      label: 'Digital Engineering',
      icon: '▦',
      description:
        'Engineering models, detailed drawings, connection designs, and fabrication information are developed using modern digital workflows.',
    },
    {
      number: '06',
      title: 'Deliver',
      label: 'Engineered Reality',
      icon: '◆',
      description:
        'The final result is a coordinated, technically sound, construction-ready engineering solution that can move confidently toward execution.',
    },
  ];

  const styles = {
    section: {
      position: 'relative',
      width: '100%',
      minHeight: '1100px',
      padding: '9rem 5% 8rem',
      background:
        'linear-gradient(135deg, #ffffff 0%, #f7fbff 48%, #edf8ff 100%)',
      overflow: 'hidden',
      fontFamily:
        '"Inter", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      boxSizing: 'border-box',
    },

    backgroundGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'linear-gradient(rgba(2,132,199,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(2,132,199,0.035) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
      animation: 'engineeringGrid 25s linear infinite',
      pointerEvents: 'none',
    },

    glowTop: {
      position: 'absolute',
      width: '600px',
      height: '600px',
      borderRadius: '50%',
      top: '-300px',
      right: '-180px',
      background: 'rgba(14,165,233,0.10)',
      filter: 'blur(100px)',
      animation: 'engineeringGlow 9s ease-in-out infinite',
    },

    glowBottom: {
      position: 'absolute',
      width: '500px',
      height: '500px',
      borderRadius: '50%',
      bottom: '-250px',
      left: '-200px',
      background: 'rgba(2,132,199,0.08)',
      filter: 'blur(100px)',
      animation: 'engineeringGlow2 11s ease-in-out infinite',
    },

    container: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '1400px',
      margin: '0 auto',
    },

    header: {
      maxWidth: '950px',
      margin: '0 auto',
      textAlign: 'center',
    },

    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: '9px 18px',
      background: colors.bgBlue,
      border: `1px solid ${colors.border}`,
      borderRadius: '999px',
      color: colors.blueDark,
      fontSize: '0.75rem',
      fontWeight: '800',
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      marginBottom: '1.8rem',
    },

    badgeDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: colors.blue,
      animation: 'pulseEngineering 2s infinite',
    },

    heading: {
      margin: 0,
      color: colors.navy,
      fontSize: 'clamp(3.2rem, 7vw, 6.5rem)',
      lineHeight: '0.94',
      letterSpacing: '-0.065em',
      fontWeight: '850',
    },

    headingAccent: {
      display: 'inline-block',
      color: colors.blue,
      position: 'relative',
    },

    underline: {
      position: 'absolute',
      height: '9px',
      left: '2%',
      right: '2%',
      bottom: '-12px',
      borderRadius: '20px',
      background: '#dff2ff',
      zIndex: -1,
    },

    intro: {
      maxWidth: '760px',
      margin: '2.2rem auto 0',
      color: colors.slate,
      fontSize: '1.12rem',
      lineHeight: '1.8',
    },

    workflow: {
      position: 'relative',
      marginTop: '7rem',
    },

    workflowHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      marginBottom: '3rem',
      gap: '2rem',
    },

    smallHeading: {
      margin: 0,
      fontSize: '0.78rem',
      fontWeight: '800',
      color: colors.blue,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    },

    workflowTitle: {
      margin: '0.5rem 0 0',
      color: colors.navy,
      fontSize: '2rem',
      fontWeight: '800',
      letterSpacing: '-0.035em',
    },

    workflowDescription: {
      maxWidth: '420px',
      margin: 0,
      color: colors.muted,
      fontSize: '0.9rem',
      lineHeight: '1.65',
      textAlign: 'right',
    },

    line: {
      position: 'absolute',
      top: '67px',
      left: '7%',
      right: '7%',
      height: '2px',
      background:
        'linear-gradient(90deg, transparent, #7dd3fc, #0284c7, #7dd3fc, transparent)',
      zIndex: 0,
    },

    lineTravel: {
      position: 'absolute',
      height: '100%',
      width: '150px',
      background:
        'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)',
      animation: 'lineTravel 3s linear infinite',
    },

    stageGrid: {
      position: 'relative',
      zIndex: 2,
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '1rem',
    },

    stage: (active) => ({
      textAlign: 'center',
      cursor: 'pointer',
      transform: active ? 'translateY(-14px)' : 'translateY(0)',
      transition: 'all 0.45s cubic-bezier(.2,.8,.2,1)',
    }),

    stageCircle: (active) => ({
      position: 'relative',
      width: '130px',
      height: '130px',
      margin: '0 auto 1.5rem',
      borderRadius: '50%',
      background: active
        ? 'linear-gradient(145deg, #0284c7, #075985)'
        : '#ffffff',
      border: `2px solid ${active ? colors.blue : '#cfe7f5'}`,
      color: active ? '#ffffff' : colors.blue,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2.3rem',
      boxShadow: active
        ? '0 25px 50px rgba(2,132,199,0.25), 0 0 0 12px rgba(2,132,199,0.07)'
        : '0 15px 35px rgba(15,23,42,0.07)',
      transition: 'all .45s ease',
    }),

    stageNumber: {
      position: 'absolute',
      top: '-7px',
      right: '-5px',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: colors.navy,
      border: '3px solid #ffffff',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.65rem',
      fontWeight: '800',
    },

    stageTitle: (active) => ({
      margin: 0,
      color: active ? colors.blue : colors.navy,
      fontSize: '1.05rem',
      fontWeight: '800',
      transition: 'color .3s ease',
    }),

    stageLabel: {
      marginTop: '6px',
      color: colors.muted,
      fontSize: '0.68rem',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
    },

    intelligence: {
      marginTop: '7rem',
      display: 'grid',
      gridTemplateColumns: '0.85fr 1.15fr',
      gap: '2rem',
    },

    visualPanel: {
      minHeight: '420px',
      borderRadius: '32px',
      padding: '2.5rem',
      position: 'relative',
      overflow: 'hidden',
      background:
        'linear-gradient(145deg, #0f172a 0%, #123b56 55%, #075985 100%)',
      boxShadow: '0 30px 70px rgba(15,23,42,0.18)',
    },

    technicalPattern: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'linear-gradient(90deg, rgba(125,211,252,0.08) 1px, transparent 1px), linear-gradient(rgba(125,211,252,0.08) 1px, transparent 1px)',
      backgroundSize: '45px 45px',
      animation: 'technicalPattern 18s linear infinite',
    },

    blueprintCircle: {
      position: 'absolute',
      width: '310px',
      height: '310px',
      borderRadius: '50%',
      border: '1px solid rgba(125,211,252,0.3)',
      top: '55px',
      right: '-50px',
      animation: 'blueprintRotate 20s linear infinite',
    },

    blueprintCircle2: {
      position: 'absolute',
      width: '210px',
      height: '210px',
      borderRadius: '50%',
      border: '1px dashed rgba(125,211,252,0.3)',
      top: '105px',
      right: '0px',
      animation: 'blueprintRotateReverse 14s linear infinite',
    },

    blueprintCross: {
      position: 'absolute',
      top: '204px',
      right: '55px',
      width: '180px',
      height: '1px',
      background: 'rgba(125,211,252,0.35)',
      transform: 'rotate(-25deg)',
    },

    visualContent: {
      position: 'relative',
      zIndex: 3,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },

    visualEyebrow: {
      color: '#7dd3fc',
      fontSize: '0.7rem',
      fontWeight: '800',
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
    },

    visualTitle: {
      margin: '0.6rem 0 0',
      color: '#ffffff',
      fontSize: '2.8rem',
      lineHeight: '1',
      fontWeight: '800',
      letterSpacing: '-0.04em',
    },

    visualText: {
      margin: '1rem 0 0',
      color: '#cbd5e1',
      maxWidth: '450px',
      lineHeight: '1.7',
      fontSize: '0.9rem',
    },

    inputOutputPanel: {
      background: '#ffffff',
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '32px',
      padding: '2.5rem',
      boxShadow: '0 20px 55px rgba(15,23,42,0.07)',
    },

    panelTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '2rem',
    },

    panelTitle: {
      margin: 0,
      color: colors.navy,
      fontSize: '1.5rem',
      fontWeight: '800',
    },

    activeTag: {
      padding: '7px 12px',
      borderRadius: '999px',
      background: '#ecfeff',
      color: colors.green,
      fontSize: '0.68rem',
      fontWeight: '800',
    },

    fieldSection: {
      marginBottom: '1.8rem',
    },

    fieldLabel: {
      display: 'block',
      marginBottom: '0.8rem',
      color: colors.muted,
      fontSize: '0.7rem',
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
    },

    fields: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '0.75rem',
    },

    field: {
      padding: '1rem',
      borderRadius: '13px',
      background: colors.bg,
      border: `1px solid ${colors.borderLight}`,
      color: colors.navy,
      fontSize: '0.82rem',
      fontWeight: '700',
      transition: 'all .3s ease',
    },

    outputArea: {
      borderTop: `1px solid ${colors.borderLight}`,
      paddingTop: '1.8rem',
    },

    outputGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '0.7rem',
    },

    output: {
      padding: '1rem 0.7rem',
      textAlign: 'center',
      borderRadius: '12px',
      background: colors.bgBlue,
      color: colors.blueDark,
      fontSize: '0.72rem',
      fontWeight: '800',
    },

    metrics: {
      marginTop: '2rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '1rem',
    },

    metric: {
      padding: '1.2rem',
      borderRadius: '16px',
      background: colors.bg,
      border: `1px solid ${colors.borderLight}`,
    },

    metricValue: {
      fontSize: '1.5rem',
      fontWeight: '850',
      color: colors.navy,
    },

    metricLabel: {
      marginTop: '4px',
      color: colors.muted,
      fontSize: '0.68rem',
      fontWeight: '700',
      textTransform: 'uppercase',
    },
  };

  return (
    <section style={styles.section}>
      <style>{`
        @keyframes engineeringGrid {
          0% { background-position: 0 0; }
          100% { background-position: 48px 48px; }
        }

        @keyframes engineeringGlow {
          0%,100% { transform: scale(1); }
          50% { transform: scale(1.15) translate(-30px,30px); }
        }

        @keyframes engineeringGlow2 {
          0%,100% { transform: translate(0,0); }
          50% { transform: translate(40px,-30px); }
        }

        @keyframes pulseEngineering {
          0%,100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(2,132,199,.3);
          }
          50% {
            transform: scale(1.4);
            box-shadow: 0 0 0 8px rgba(2,132,199,0);
          }
        }

        @keyframes lineTravel {
          0% { left: -20%; }
          100% { left: 110%; }
        }

        @keyframes technicalPattern {
          0% { transform: translate(0,0); }
          100% { transform: translate(45px,45px); }
        }

        @keyframes blueprintRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes blueprintRotateReverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }

        @media (max-width: 1100px) {
          .engineering-stage-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 3rem 1rem !important;
          }

          .engineering-line {
            display: none !important;
          }

          .engineering-intelligence {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 700px) {
          .engineering-main-section {
            padding: 6rem 5% !important;
            min-height: auto !important;
          }

          .engineering-stage-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 2.5rem 1rem !important;
          }

          .engineering-workflow-header {
            flex-direction: column !important;
            align-items: flex-start !important;
          }

          .engineering-workflow-description {
            text-align: left !important;
          }

          .engineering-stage-circle {
            width: 90px !important;
            height: 90px !important;
            font-size: 1.8rem !important;
          }

          .engineering-visual {
            min-height: 330px !important;
          }

          .engineering-fields {
            grid-template-columns: 1fr !important;
          }

          .engineering-output-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          .engineering-metrics {
            grid-template-columns: 1fr !important;
          }

          .engineering-heading {
            font-size: 3rem !important;
          }
        }

        @media (max-width: 420px) {
          .engineering-stage-grid {
            gap: 2rem .5rem !important;
          }

          .engineering-stage-circle {
            width: 78px !important;
            height: 78px !important;
            font-size: 1.5rem !important;
          }

          .engineering-stage-title {
            font-size: .9rem !important;
          }

          .engineering-panel {
            padding: 1.4rem !important;
          }
        }
      `}</style>

      <div style={styles.backgroundGrid} />
      <div style={styles.glowTop} />
      <div style={styles.glowBottom} />

      <div
        style={styles.container}
        className="engineering-main-section"
      >

        {/* ================= HEADER ================= */}

        <div style={styles.header}>
          <div style={styles.badge}>
            <span style={styles.badgeDot} />
            Engineering Response
          </div>

          <h2
            style={styles.heading}
            className="engineering-heading"
          >
            From Your Idea
            <br />
            To{' '}
            <span style={styles.headingAccent}>
              Engineered Reality.
              <span style={styles.underline} />
            </span>
          </h2>

          <p style={styles.intro}>
            We transform project concepts into intelligent engineering
            solutions through a structured process of understanding,
            investigation, analysis, design, digital modelling, and
            technical delivery.
          </p>
        </div>

        {/* ================= WORKFLOW ================= */}

        <div style={styles.workflow}>

          <div
            style={styles.workflowHeader}
            className="engineering-workflow-header"
          >
            <div>
              <p style={styles.smallHeading}>
                Our Engineering Process
              </p>

              <h3 style={styles.workflowTitle}>
                Six stages. One engineered outcome.
              </h3>
            </div>

            <p
              style={styles.workflowDescription}
              className="engineering-workflow-description"
            >
              Every stage builds on the previous one, creating a controlled
              path from initial concept to construction-ready engineering.
            </p>
          </div>

          <div
            style={styles.line}
            className="engineering-line"
          >
            <div style={styles.lineTravel} />
          </div>

          <div
            style={styles.stageGrid}
            className="engineering-stage-grid"
          >
            {stages.map((stage, index) => {
              const active = activeStep === index;

              return (
                <div
                  key={stage.number}
                  style={styles.stage(active)}
                  onMouseEnter={() => setActiveStep(index)}
                >
                  <div
                    style={styles.stageCircle(active)}
                    className="engineering-stage-circle"
                  >
                    {stage.icon}

                    <span style={styles.stageNumber}>
                      {stage.number}
                    </span>
                  </div>

                  <h4
                    style={styles.stageTitle(active)}
                    className="engineering-stage-title"
                  >
                    {stage.title}
                  </h4>

                  <div style={styles.stageLabel}>
                    {stage.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ================= ENGINEERING INTELLIGENCE ================= */}

        <div
          style={styles.intelligence}
          className="engineering-intelligence"
        >

          {/* LEFT VISUAL */}

          <div
            style={styles.visualPanel}
            className="engineering-visual"
          >
            <div style={styles.technicalPattern} />
            <div style={styles.blueprintCircle} />
            <div style={styles.blueprintCircle2} />
            <div style={styles.blueprintCross} />

            <div style={styles.visualContent}>
              <div style={styles.visualEyebrow}>
                Active Engineering Stage
              </div>

              <h3 style={styles.visualTitle}>
                {stages[activeStep].title}
                <br />
                / {stages[activeStep].number}
              </h3>

              <p style={styles.visualText}>
                {stages[activeStep].description}
              </p>
            </div>
          </div>

          {/* RIGHT INFORMATION */}

          <div
            style={styles.inputOutputPanel}
            className="engineering-panel"
          >

            <div style={styles.panelTop}>
              <h3 style={styles.panelTitle}>
                Engineering Intelligence
              </h3>

              <span style={styles.activeTag}>
                LIVE WORKFLOW
              </span>
            </div>

            {/* INPUTS */}

            <div style={styles.fieldSection}>
              <span style={styles.fieldLabel}>
                Project Inputs
              </span>

              <div
                style={styles.fields}
                className="engineering-fields"
              >
                {[
                  'Project Requirements',
                  'Design Codes',
                  'Structural Loads',
                  'Material Selection',
                  'Architectural Data',
                  'Site Conditions',
                ].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.field,
                      borderColor:
                        activeStep >= 1
                          ? colors.border
                          : colors.borderLight,
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* OUTPUTS */}

            <div style={styles.outputArea}>
              <span style={styles.fieldLabel}>
                Engineering Outputs
              </span>

              <div
                style={styles.outputGrid}
                className="engineering-output-grid"
              >
                {[
                  'Analysis',
                  'Design',
                  'Connections',
                  '3D Model',
                  'Shop Drawings',
                  'CAD Plans',
                  'Reports',
                  'Execution Data',
                ].map((item, index) => (
                  <div
                    key={index}
                    style={styles.output}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* METRICS */}

            <div
              style={styles.metrics}
              className="engineering-metrics"
            >
              <div style={styles.metric}>
                <div style={styles.metricValue}>
                  01
                </div>

                <div style={styles.metricLabel}>
                  Integrated Workflow
                </div>
              </div>

              <div style={styles.metric}>
                <div style={styles.metricValue}>
                  06
                </div>

                <div style={styles.metricLabel}>
                  Engineering Stages
                </div>
              </div>

              <div style={styles.metric}>
                <div style={styles.metricValue}>
                  360°
                </div>

                <div style={styles.metricLabel}>
                  Project Approach
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default NewEngineeringResponse;